import {useEffect, useState} from 'react';
import Select from 'react-select';
import url from '../../../../utils/url';
import styles from './styles.module.css';
import http from 'services/http';
import {alerta} from '../../../../components/Alert';
import {moedaInput, parseMoeda} from '../../../../utils/moedaInput';

const LIMITE_INFO = 100;

export default function CriarSorteio({history}) {
    const [tipo, setTipo] = useState('');
    const [premio, setPremio] = useState('');
    const [valorPremio, setValorPremio] = useState('');
    const [valorMinimo, setValorMinimo] = useState('');
    const [data, setData] = useState('');
    const [hora, setHora] = useState('');
    const [info, setInfo] = useState('');
    const [modalidades, setModalidades] = useState([]);
    const [gerentes, setGerentes] = useState([]);

    const [premios, setPremios] = useState([]);
    const [tipos, setTipos] = useState([]);
    const [modalidadesDisponiveis, setModalidadesDisponiveis] = useState([]);
    const [premioPix, setPremioPix] = useState(null);
    const [opcoesGerentes, setOpcoesGerentes] = useState([]);
    const [salvando, setSalvando] = useState(false);

    // Só o PIX é pago em dinheiro: nele a tela pede o valor, e nos outros
    // prêmios pede a descrição em "Informações".
    const ehPix = premioPix !== null && Number(premio) === Number(premioPix);

    useEffect(() => {
        const carregarPremios = async () => {
            try {
                const {data: {data: lista}} = await http.get('/sorteios/premios');
                setPremios(lista ?? []);
            } catch (e) {
                alerta(e.response?.data ?? {success: false, message: 'Erro ao carregar prêmios.'});
            }
        };

        const carregarOpcoes = async () => {
            try {
                const {data: {data: opcoes}} = await http.get('/sorteios/opcoes');
                setTipos(opcoes.tipos ?? []);
                setModalidadesDisponiveis(opcoes.modalidades ?? []);
                setPremioPix(opcoes.premio_pix ?? null);
            } catch (e) {
                alerta(e.response?.data ?? {success: false, message: 'Erro ao carregar as opções do sorteio.'});
            }
        };

        const carregarGerentes = async () => {
            try {
                const {data: {data: lista}} = await http.get('/usuarios', {
                    params: {nivel: 'gerente', resumida: true},
                });
                setOpcoesGerentes(
                    (lista ?? []).map((g) => ({value: g.id, label: (g.nome ?? g.login ?? '').toUpperCase()}))
                );
            } catch (e) {
                alerta(e.response?.data ?? {success: false, message: 'Erro ao carregar gerentes.'});
            }
        };

        carregarPremios();
        carregarOpcoes();
        carregarGerentes();
    }, []);

    const marcarModalidade = (chave, marcada) => {
        setModalidades((atuais) => (
            marcada ? [...atuais, chave] : atuais.filter((c) => c !== chave)
        ));
    };

    const premioSelecionado = premios.find((item) => String(item.key) === String(premio));

    const validar = () => {
        if (!premio) return 'Escolha o prêmio!';
        if (ehPix && parseMoeda(valorPremio) <= 0) return 'Informe o valor do PIX!';
        if (!data || !hora) return 'Informe a data e o horário do sorteio!';
        if (modalidades.length === 0) return 'Marque ao menos uma modalidade participante!';
        if (gerentes.length === 0) return 'Selecione ao menos um gerente participante!';
        return null;
    };

    const salvarSorteio = async () => {
        const erro = validar();

        if (erro) {
            alerta({success: false, message: erro});
            return;
        }

        setSalvando(true);

        try {
            const res = await http.post('/sorteios', {
                premio: Number(premio),
                tipo,
                valor_premio: ehPix ? parseMoeda(valorPremio) : null,
                valor_minimo: parseMoeda(valorMinimo),
                data_sorteio: `${data} ${hora}:00`,
                info: ehPix ? null : info,
                modalidades,
                gerentes: gerentes.map((g) => g.value),
            });

            alerta(res.data);
            history.push('/sorteio');
        } catch (error) {
            alerta(error.response?.data ?? {success: false, message: 'Erro ao criar sorteio.'});
        } finally {
            setSalvando(false);
        }
    };

    return (
        <>
            <div className="titulo-principal">Criar Sorteio</div>
            <div className={styles.conteudoPrincipal}>
                <div className={styles.fields_form}>
                    <div className={styles.inputs}>
                        <div className={styles.formGroup}>
                            <div className={styles.label}>
                                <label>Tipo do sorteio:</label>
                            </div>
                            <div className={styles.selectField}>
                                <select
                                    style={{width: 200}}
                                    value={tipo}
                                    onChange={(e) => setTipo(e.target.value)}
                                    className="form-control"
                                >
                                    <option value=""></option>
                                    {tipos.map((item) => (
                                        <option key={item.value} value={item.value}>{item.label}</option>
                                    ))}
                                </select>
                                <small className="text-muted">
                                    {tipo
                                        ? `Cada bilhete que participa recebe um número diferente, até ${tipo === '5_digitos' ? '100.000' : '10.000'} bilhetes. Ganha quem tiver o número lançado.`
                                        : 'Sem escolher, o sistema sorteia sozinho, como sempre fez.'}
                                </small>
                            </div>
                        </div>

                        <div className={styles.formGroup}>
                            <div className={styles.label}>
                                <label>Premio:</label>
                            </div>
                            <div className={styles.selectField}>
                                <select
                                    style={{width: 200}}
                                    value={premio}
                                    onChange={(e) => setPremio(e.target.value)}
                                    className="form-control"
                                >
                                    <option value=""></option>
                                    {premios.map((item) => (
                                        <option key={item.key} value={item.key}>{item.value}</option>
                                    ))}
                                </select>
                            </div>
                        </div>

                        {ehPix && (
                            <div className={styles.formGroup}>
                                <div className={styles.label}>
                                    <label>Valor do PIX:</label>
                                </div>
                                <div className={styles.selectField}>
                                    <input
                                        type="text"
                                        inputMode="numeric"
                                        className="form-control"
                                        style={{width: 200}}
                                        placeholder="0,00"
                                        value={valorPremio}
                                        onChange={(e) => setValorPremio(moedaInput(e.target.value))}
                                    />
                                </div>
                            </div>
                        )}

                        <div className={styles.formGroup}>
                            <div className={styles.label}>
                                <label>Valor mínimo do bilhete:</label>
                            </div>
                            <div className={styles.selectField}>
                                <input
                                    type="text"
                                    inputMode="numeric"
                                    className="form-control"
                                    style={{width: 200}}
                                    placeholder="0,00"
                                    value={valorMinimo}
                                    onChange={(e) => setValorMinimo(moedaInput(e.target.value))}
                                />
                            </div>
                        </div>

                        <div className={styles.formGroup}>
                            <div className={styles.label}>
                                <label>Data do Sorteio:</label>
                            </div>
                            <div className={styles.selectField}>
                                <input
                                    style={{width: 200}}
                                    type="date"
                                    value={data}
                                    onChange={(e) => setData(e.target.value)}
                                    className="form-control"
                                />
                            </div>
                        </div>

                        <div className={styles.formGroup}>
                            <div className={styles.label}>
                                <label className="control-label">Encerra</label>
                            </div>
                            <div className={styles.selectField}>
                                <input
                                    style={{width: 200}}
                                    type="time"
                                    value={hora}
                                    onChange={(e) => setHora(e.target.value)}
                                    className="form-control"
                                />
                            </div>
                        </div>

                        <div className={styles.formGroup}>
                            <div className={styles.label}>
                                <label>Gerentes participantes:</label>
                            </div>
                            <div className={styles.selectField} style={{maxWidth: 320}}>
                                <Select
                                    isMulti
                                    placeholder="Selecione os gerentes..."
                                    noOptionsMessage={() => 'Nenhum gerente'}
                                    options={opcoesGerentes}
                                    value={gerentes}
                                    onChange={(selecionados) => setGerentes(selecionados || [])}
                                />
                                {/* Marcado enquanto todos estiverem na lista: tirar um no select desmarca. */}
                                <div className="form-check mt-2">
                                    <input
                                        type="checkbox"
                                        className="form-check-input"
                                        id="gerentes-todos"
                                        disabled={opcoesGerentes.length === 0}
                                        checked={opcoesGerentes.length > 0 && gerentes.length === opcoesGerentes.length}
                                        onChange={(e) => setGerentes(e.target.checked ? opcoesGerentes : [])}
                                    />
                                    <label className="form-check-label" htmlFor="gerentes-todos">
                                        Selecionar todos
                                    </label>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className={styles.img}>
                        {premioSelecionado ? (
                            <i className={`fa fa-5x ${premioSelecionado.icon}`} aria-hidden="true"></i>
                        ) : (
                            <img src={`${url}/storage/bancas/logo.jpeg`} width={300} height={200} alt="Prêmio"/>
                        )}
                    </div>
                </div>

                <div className={styles.modalidades}>
                    <div className={styles.label}>
                        <label>Modalidades participantes:</label>
                    </div>
                    <div className={styles.checkboxes}>
                        {modalidadesDisponiveis.map((modalidade) => (
                            <div key={modalidade.chave} className={`form-check ${styles.checkbox}`}>
                                <input
                                    type="checkbox"
                                    className="form-check-input"
                                    id={`modalidade-${modalidade.chave}`}
                                    checked={modalidades.includes(modalidade.chave)}
                                    onChange={(e) => marcarModalidade(modalidade.chave, e.target.checked)}
                                />
                                <label className="form-check-label" htmlFor={`modalidade-${modalidade.chave}`}>
                                    {modalidade.nome}
                                </label>
                            </div>
                        ))}
                    </div>
                </div>

                {!ehPix && (
                    <div className={styles.textSorteio}>
                        <div className={styles.formGroup}>
                            <div className={styles.label}>
                                <label>Informações:</label>
                            </div>
                            <div className={styles.selectField}>
                                <textarea
                                    className="form-control"
                                    style={{width: 200}}
                                    rows="5"
                                    maxLength={LIMITE_INFO}
                                    value={info}
                                    onChange={(e) => setInfo(e.target.value)}
                                />
                                <small className="text-muted">{info.length}/{LIMITE_INFO} caracteres</small>
                            </div>
                        </div>
                    </div>
                )}

                <div className={styles.description}>
                    <div className={styles.formGroup}>
                        {premioSelecionado ? (
                            <span>Concorre ao prêmio: <span>{premioSelecionado.value}</span></span>
                        ) : <></>}
                    </div>
                    <div className={styles.formGroup}>
                        <p>{ehPix ? (valorPremio && `R$ ${valorPremio}`) : info}</p>
                    </div>
                </div>

                <div className={styles.buttonStyle}>
                    <button
                        className="btn btn-success"
                        onClick={salvarSorteio}
                        disabled={salvando}
                    >
                        {salvando ? 'Salvando...' : 'Criar Sorteio'}
                    </button>
                </div>
            </div>
        </>
    );
}
